import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ITimesTemp, ThemeService } from '@wind-log_library/fe-component-library';
import { getDateFormat } from 'app/shared/helper/date-format.helper';
import { InitialDataService } from 'app/shared/services/data-services/initial-data/initial-data.service';
import { DateTimeFormatService } from 'app/shared/services/date-time-format/date-time-format.service';
import { UserPreferencesUnitSettingsService } from 'app/shared/services/user-preferences-unit-settings/user-preferences-unit-settings.service';
import moment, { unitOfTime } from 'moment';
import { BsDatepickerConfig, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { defineLocale} from 'ngx-bootstrap/chronos';
import * as locale from 'ngx-bootstrap/locale';

import { Observable, ReplaySubject, combineLatest, of } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { LanguageService } from 'app/shared/services/language/language.service';

@Component({
    selector: 'app-month-picker',
    templateUrl: './month-picker.component.html',
    styleUrls: ['./month-picker.component.scss'],
    standalone: false
})
export class MonthPickerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() pickedDay: Date;
  @Input() type: string;
  @Output() changeDate = new EventEmitter<Date>();

  public minDate;
  public maxDate = new Date();
  public bsConfig: Observable<Partial<BsDatepickerConfig>>;
  public disabledLeft = false;
  public disabledRight = false;
  private unsubscriber = new ReplaySubject();

  constructor(
    private themeService: ThemeService,
    private dateTimeFormatService: DateTimeFormatService,
    private userPreferencesUnitSettingsService: UserPreferencesUnitSettingsService,
    private initialDataService: InitialDataService,
    private localeService: BsLocaleService,
    private translateService: TranslateService,
    private languageService: LanguageService
  ) {}

  public ngOnInit() {
    this.initialDataService.getFirstAvailableDataAsDate().subscribe(firstAvTs => {
      this.minDate = firstAvTs;
    });
    this.subscribeLanguageChange();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.type) {
      this.bsConfig = combineLatest([
        this.type,
        this.themeService.getTheme(),
        this.userPreferencesUnitSettingsService.unitPreference$,
      ])
      .pipe(
        takeUntil(this.unsubscriber),
        map(([type, theme]) => ({
          showWeekNumbers: false,
          containerClass: theme,
          dateInputFormat: this.getDateFormat().toLocaleUpperCase(),
        }))
      );
    }

    if (changes.pickedDay || changes.type) {
      this.disabledLeft = this.step('subtract') < this.minDate;
      this.disabledRight = this.step('add') > this.maxDate;
    }
  }

  public ngOnDestroy() {
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  public getDateFormat() {
    return this.dateTimeFormatService.getDateFormat(getDateFormat(this.type))
  }

  public changeDay(event: Date) {
    if (event.getTime() !== this.pickedDay?.getTime()) {
      this.changeDate.emit(new Date(event));
    }
  }

  public moveLeft() {
    this.changeDay(this.step('subtract'));
  }
  public moveRight() {
    this.changeDay(this.step('add'));
  }

  private step(operation: 'add' | 'subtract'): Date {
    const unit = this.minMode as unitOfTime.DurationConstructor;
    return moment(this.pickedDay)[operation](1, unit).startOf(unit).toDate();
  }

  get minMode() {
    return {
      'hours': 'day',
      'days': 'month',
      'months': 'year'
    }[this.type] || 'day';
  }

  private subscribeLanguageChange() {
    this.languageService.updateCalendars(this.translateService.currentLang,this.localeService);
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe(event => {
      if (event) {
        this.languageService.updateCalendars(event.lang,this.localeService);
      }
    });
  }

}
